import { Component, OnInit, ElementRef } from '@angular/core';
import { AnalyticsService, getDefaultDateRange, getGeneralStatCards, StatCard, getStartOfMonth, isCompletionEnabled, isScoringEnabled, PopularExperience } from '@shared/services/analytics.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { Course } from '@shared/models/course.model';
import { Learnspace } from '@shared/models/learnspace.model';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { CurrentHubService } from '@shared/services/currentHub.service';
import { LoadState } from '../loader-directive/loader.module';
import { formatNumber, formatPercent } from '@angular/common';
import { courseLabel } from '@shared/utils/wording.utils';

@Component({
  templateUrl: './learnspace-analytics.component.html',
  styleUrls: ['./learnspace-analytics.component.scss']
})
export class LearnspaceAnalyticsComponent implements OnInit {

  public learnspace: Learnspace;
  public popularExperiences: PopularExperience[];
  private page = 0;
  private pageSize = 5;
  private _scrollSubs: Subscription;
  public loadStatus = LoadState.pending;
  date_from: Date;  // TODO: Default value 1 year
  date_to: Date;
  recentCouseCount: number;
  public scoringEnabledLearnspace: boolean;
  public completionEnabledLearnspace: boolean;

  public statsCards: StatCard[];
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _courseAPIService: CoursesAPIService,
    private _router: Router,
    private _analyticsService: AnalyticsService,
    private _elRef: ElementRef,

  ) {

  }

  ngOnInit(): void {
    const defaultRange = getDefaultDateRange();
    this.date_from = defaultRange.date_from;
    this.date_to = defaultRange.date_to;
    this._activatedRoute.paramMap.subscribe(params => {
      const learnspaceId = params.get('lsId');
      // Get course content
      this._courseAPIService.getLearnspaceById(learnspaceId).subscribe(async (learnspace) => {
        this.learnspace = learnspace;
        this.completionEnabledLearnspace = this.isCompletionEnabledLearnspace();
        this.scoringEnabledLearnspace = this.isScoringEnabledLearnspace();

        this.updateLearnspaceStats();
        this.popularExperiences = await this.loadPopularExperiences().toPromise();
        this.initScroll();
      });
    });
    this._activatedRoute.queryParams.subscribe(async params => {
      let dateRangeChange = false;
      if (params.dateFrom && this.date_from && this.date_from.getTime() !== parseInt(params.dateFrom)) {
        this.date_from = new Date(parseInt(params.dateFrom))
        dateRangeChange = true;
      }
      if (params.dateTo && this.date_to && this.date_to.getTime() !== parseInt(params.dateTo)) {
        this.date_to = new Date(parseInt(params.dateTo))
        dateRangeChange = true;
      }
      if (params.timePickerMode && params.timePickerMode === 'forever') {
        this.date_from = null;
        this.date_to = null;
        dateRangeChange = true;
      }
      if (this.learnspace && dateRangeChange) {
        this.updateLearnspaceStats();
        this.page = 0;
        this.popularExperiences = await this.loadPopularExperiences().toPromise();
      }
    })
  }
  onTimePeriodChange(range: { date_from: Date, date_to: Date }) {
    this.date_from = range.date_from;
    this.date_to = range.date_to;
    this.updateLearnspaceStats();
  }

  async updateLearnspaceStats(): Promise<void> {
    this.loadStatus = LoadState.loading;
    let learnspaceStats;
    try {
      learnspaceStats = await this._analyticsService.getLearnspaceStats(this.learnspace.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
    }
    catch (e) {
      this.loadStatus = LoadState.error;
    }
    const xpStatCard = {
      icon: 'fa-circle-plus',
      label: `<span class="figure">${learnspaceStats?.activeExperiences || 0} </span>&nbsp;<span class="label"> active XP</span>`,
      sublabel: `${this.learnspace.courses.length} ${courseLabel(this.learnspace.mode, true)} in total`
    }

    const visitorStatCard = {
      icon: 'fa-users',
      label: `<span class="figure">${learnspaceStats?.userCount || 0} </span>&nbsp;<span class="label">${(!isNaN(learnspaceStats?.userCount) && learnspaceStats?.userCount) > 1 ? ' Visitors' : ' Visitor'}</span>`,
      sublabel: 'including ' + (learnspaceStats?.anonymousUsersCount || 0) + ' guests'
    }
    const sessionStatCard = {
      icon: 'fa-eye',
      label: `<span class="figure">${learnspaceStats?.sessionCount} </span>&nbsp;<span class="label"> sessions</span>`,
      sublabel: `${Math.floor((learnspaceStats?.sessionsPerVisitor || 0))} sessions per visitor`
    }


    const statCards = [xpStatCard, visitorStatCard, sessionStatCard]
    if (learnspaceStats && learnspaceStats.totalAIVoiceMinutes > 0) {
      const aiStatCard = {
        icon: 'fa-message',
        label: `<span class="figure"> ${Math.ceil((learnspaceStats?.totalAIVoiceMinutes || 0))}</span>&nbsp;<span class="label"> AI Voice Min </span>`,
        sublabel: `${Math.ceil((learnspaceStats?.avgAIVoiceMinutes || 0))} min/session`
      }
      statCards.push(aiStatCard)
    }
    // if (this.completionEnabledLearnspace) {
    //   statCards.push({
    //     icon: 'fa-circle-check',
    //     label: `<span class="figure figure-time">${formatPercent(learnspaceStats.completedRatio || 0, 'en-US')} </span> <span class="label"> completed</span>`,
    //     sublabel: ``
    //   })
    // }
    if (this.scoringEnabledLearnspace) {
      statCards.push({
        icon: 'fa-circle-check',
        label: `<span class="figure">${formatPercent(learnspaceStats?.passedRatio || 0, 'en-US')}</span>&nbsp;<span class="label">passed</span>`,
        sublabel: ``
      })
    }

    this.loadStatus = LoadState.completed;
    this.statsCards = statCards;
  }
  initScroll(): void {
    const scrolledElm = this._elRef.nativeElement.parentElement;
    this._scrollSubs = fromEvent(scrolledElm, 'scroll')
      .pipe(
        throttleTime(100),
      ).subscribe(async () => {
        const leftToScroll = scrolledElm.scrollHeight - scrolledElm.clientHeight - scrolledElm.scrollTop; // at bottom clientHeight + scrollTop = scrollHeight
        const threshold = scrolledElm.scrollHeight * 0.1; // 10% should be computed once
        if (leftToScroll < threshold) {
          this.page++;
          const nextPopularExperiences = await this.loadPopularExperiences(this.pageSize, this.page * this.pageSize).toPromise(); //this.learnspace.courses.slice(this.page * this.pageSize, (this.page + 1) * this.pageSize);
          if (nextPopularExperiences && nextPopularExperiences.length > 0) {
            this.popularExperiences = this.popularExperiences.concat(nextPopularExperiences);
          }
        }
      });
  }
  loadPopularExperiences(limit: number = 5, offset: number = 0): Observable<PopularExperience[]> {
    return this._analyticsService.getLearnspacePopularExperiences(this.learnspace.id, { dateFrom: this.date_from, dateTo: this.date_to, limit: limit, offset: offset})
  }
  goToCourseAnalytics(course: Course): void {
    const url = `${this._router.url}/courses/${course.id}`;
    this._router.navigate([url], { queryParamsHandling: "merge" });
  }

  isCompletionEnabledLearnspace(): boolean {
    if (!this.learnspace.courses) return false;
    return this.learnspace.courses.some(isCompletionEnabled)
  }
  isScoringEnabledLearnspace(): boolean {
    if (!this.learnspace.courses) return false;
    return this.learnspace.courses.some(isScoringEnabled)
  }
  openHelp(): void {
    window.open('http://help.spaces.wondavr.com/en/articles/3294026-analytics-dashboard');
  }
}
